import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  AudioLines,
  Headphones,
  Mic,
  RefreshCw,
  Square,
  Volume2,
} from 'lucide-react';
import {
  audioDeviceManager,
  type AudioDeviceSnapshot,
} from '../AudioDeviceManager';

const INITIAL_AUDIO_SNAPSHOT = audioDeviceManager.getSnapshot();

function deviceName(
  device: MediaDeviceInfo,
  index: number,
  prefix: string,
): string {
  return String(device.label || `${prefix} ${index + 1}`).trim();
}

export function AudioDevicePanel({ disabled = false }: { disabled?: boolean }) {
  const [snapshot, setSnapshot] = useState<AudioDeviceSnapshot>(
    INITIAL_AUDIO_SNAPSHOT,
  );
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => audioDeviceManager.subscribe(setSnapshot), []);

  useEffect(() => {
    if (!expanded) return;
    void audioDeviceManager.refreshDevices(true).catch(() => undefined);
  }, [expanded]);

  const levelPercent = useMemo(
    () => Math.round(Math.min(1, Math.max(0, snapshot.level)) * 100),
    [snapshot.level],
  );

  const run = async (action: () => Promise<unknown>) => {
    setPending(true);

    try {
      await action();
    } catch (error) {
      console.warn('[telephony] audio device action failed', error);
    } finally {
      setPending(false);
    }
  };

  return <section className="browser-phone-audio-devices">
    <button
      type="button"
      className="browser-phone-audio-toggle"
      onClick={() => setExpanded(value => !value)}
      disabled={disabled}
    >
      <Headphones />
      <span>
        <b>Гарнитура</b>
        <small>{snapshot.inputLabel}</small>
      </span>
      <AudioLines />
    </button>

    {expanded && <div className="browser-phone-audio-panel">
      <label>
        <span><Mic /> Микрофон</span>
        <select
          value={snapshot.selectedInputId}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => void run(async () => {
            await audioDeviceManager.selectInput(event.target.value);
            await audioDeviceManager.refreshDevices(false);
          })}
          disabled={pending || disabled || snapshot.testing}
        >
          <option value="">Системный микрофон</option>
          {snapshot.inputs.map((device, index) => <option
            key={device.deviceId || `input-${index}`}
            value={device.deviceId}
          >
            {deviceName(device, index, 'Микрофон')}
          </option>)}
        </select>
      </label>

      {snapshot.outputSelectionSupported && <label>
        <span><Volume2 /> Динамики или гарнитура</span>
        <select
          value={snapshot.selectedOutputId}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => void run(
            () => audioDeviceManager.selectOutput(event.target.value),
          )}
          disabled={pending || disabled}
        >
          <option value="">Системное устройство вывода</option>
          {snapshot.outputs.map((device, index) => <option
            key={device.deviceId || `output-${index}`}
            value={device.deviceId}
          >
            {deviceName(device, index, 'Устройство')}
          </option>)}
        </select>
      </label>}

      <div className="browser-phone-microphone-test">
        <div>
          <span>Уровень микрофона</span>
          <div className="browser-phone-microphone-meter" aria-label={`Уровень микрофона ${levelPercent}%`}>
            <i style={{ width: `${levelPercent}%` }} />
          </div>
          <small>
            {snapshot.trackMuted
              ? 'Устройство подключено, но дорожка отключена.'
              : snapshot.streamReady
                ? levelPercent > 2
                  ? 'Сигнал с микрофона поступает.'
                  : 'Скажите несколько слов для проверки.'
                : 'Запустите проверку перед первым звонком.'}
          </small>
        </div>

        <button
          type="button"
          className={`button ${snapshot.testing ? 'danger' : 'ghost'}`}
          onClick={() => {
            if (snapshot.testing) {
              audioDeviceManager.stopTest();
              return;
            }

            void run(() => audioDeviceManager.startTest());
          }}
          disabled={pending || disabled}
        >
          {snapshot.testing ? <><Square /> Остановить</> : <><Mic /> Проверить</>}
        </button>
      </div>

      <button
        type="button"
        className="button ghost browser-phone-audio-refresh"
        onClick={() => void run(
          () => audioDeviceManager.refreshDevices(true),
        )}
        disabled={pending || disabled}
      >
        <RefreshCw /> Обновить список устройств
      </button>

      {snapshot.error && <div className="browser-phone-audio-error">
        {snapshot.error}
      </div>}
    </div>}
  </section>;
}
